import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { Field, useFormikContext } from 'formik'
import { NotificationFormModel } from './typings'

import Autocomplete, {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  createFilterOptions,
} from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import { isValidEmail } from '@sales-center/common'

type Option = string | { email: string }

type Props = {
  options: Option[]
  placeHolder?: string
  maxItems?: number
  disabled?: boolean
  required?: boolean
}

const filter = createFilterOptions<Option>()

export function AdditionalEmployees(props: Props) {
  const {
    options,
    placeHolder,
    maxItems,
    disabled = false,
    required = false,
  } = props

  const {
    values: { additional },
    setFieldValue,
  } = useFormikContext<NotificationFormModel>()

  const [error, setError] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const onChangeInputValue = useCallback(
    (
      _event: React.ChangeEvent<unknown>,
      value: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      if (reason === 'reset') {
        if (!isValidEmail(inputValue)) {
          setError(true)
          return
        }
      }
      setError(false)
      setInputValue(value)
    },
    [inputValue],
  )

  const getOptionLabel = useCallback(
    (option: Option) => (typeof option === 'string' ? option : option.email),
    [],
  )

  const onChange = useCallback(
    (
      _event: React.ChangeEvent<unknown>,
      value: Option[],
      reason: AutocompleteChangeReason,
    ) => {
      const last = value.pop()
      if (!!last) {
        const email =
          reason === 'selectOption'
            ? typeof last === 'string'
              ? last
              : inputValue
            : getOptionLabel(last)

        if (!isValidEmail(email)) return setError(true)

        if (maxItems && value.length === maxItems) {
          setError(true)
          setTimeout(() => {
            setError(false)
          }, 4000)
          return
        }

        setInputValue('')
        setError(false)
        value.push(email)
      }

      setFieldValue('additional', value)
    },
    [getOptionLabel, inputValue, setFieldValue],
  )

  useEffect(() => {
    if (required && additional.length === 0) {
      setError(true)
    } else {
      setError(false)
    }
  }, [required, additional])

  return (
    <Field
      multiple
      value={additional}
      name={additional}
      component={Autocomplete}
      disabled={disabled}
      required={required}
      options={options}
      onChange={onChange}
      onInputChange={onChangeInputValue}
      getOptionLabel={getOptionLabel}
      renderInput={(params) => (
        <TextField
          {...params}
          label={'Non-Sales Center Employee'}
          placeholder={
            maxItems && additional.length >= maxItems
              ? ''
              : placeHolder ?? additional.length > 0
              ? 'Search'
              : 'Add Non-Sales Center Employee'
          }
          helperText={
            error &&
            (maxItems && additional.length >= maxItems
              ? `Only ${maxItems} email addresses allowed`
              : required && additional.length === 0
              ? 'Required'
              : 'Must be a valid email address!')
          }
          error={error}
          required={required}
          sx={{
            '& .MuiAutocomplete-input': {
              fontSize: '16px',
            },
          }}
          size={'medium'}
        />
      )}
      renderTags={(value, getTagProps) =>
        value.map((value, index) => (
          <Chip
            {...getTagProps({ index })}
            key={index}
            label={getOptionLabel(value)}
          />
        ))
      }
      filterSelectedOptions
      filterOptions={(options, params) => {
        const filtered = filter(options, params)
        if (params.inputValue !== '') {
          filtered.push({ email: `Add ${params.inputValue}` })
        }
        return filtered
      }}
      size="medium"
    />
  )
}

AdditionalEmployees.displayName = 'AdditionalEmployees'
